import cn from './cn.js';

const tones = {
  neutral: 'bg-gray-100 text-gray-700 ring-gray-200',
  brand: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
  info: 'bg-sky-50 text-sky-700 ring-sky-200',
  purple: 'bg-purple-50 text-purple-700 ring-purple-200',
  dark: 'bg-gray-900 text-white ring-gray-900',
  outline: 'bg-white text-gray-600 ring-gray-200',
};

const dots = {
  neutral: 'bg-gray-400',
  brand: 'bg-indigo-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-sky-500',
  purple: 'bg-purple-500',
  dark: 'bg-white',
  outline: 'bg-gray-400',
};

const sizes = {
  xs: 'px-1.5 py-px text-2xs',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
  lg: 'px-3 py-1 text-sm',
};

/**
 * Badge / pill. `tone` picks the color, `dot` adds a status dot, `pulse`
 * animates the dot (use while something is processing).
 */
export default function Badge({ tone = 'neutral', dot = false, pulse = false, size = 'md', icon: Icon, className, children, ...props }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full font-medium ring-1 ring-inset',
        tones[tone] || tones.neutral,
        sizes[size] || sizes.md,
        className,
      )}
      {...props}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dots[tone] || dots.neutral, pulse && 'ex-dot-pulse')}
          aria-hidden="true"
        />
      )}
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}

const SUCCESS = ['complete', 'completed', 'finished', 'processed', 'confirmed', 'success', 'succeeded', 'active', 'paid', 'verified', 'done', 'ok', 'healthy', 'delivered', 'sent'];
const WARNING = ['processing', 'in progress', 'in_progress', 'waiting', 'pending', 'queued', 'running', 'uploading', 'to review', 'review', 'delayed', 'retrying'];
const DANGER = ['error', 'failed', 'failure', 'rejected', 'blocked', 'canceled', 'cancelled', 'unpaid', 'overdue', 'stuck', 'down'];
const NEUTRAL = ['archived', 'inactive', 'disabled', 'draft', 'skipped', 'unknown'];

/** Maps a free-form status string to a badge tone. */
export function statusTone(status) {
  const value = String(status ?? '').trim().toLowerCase();
  if (SUCCESS.includes(value)) return 'success';
  if (WARNING.includes(value)) return 'warning';
  if (DANGER.includes(value)) return 'danger';
  if (NEUTRAL.includes(value)) return 'neutral';
  return 'neutral';
}

export function statusLabel(status) {
  const value = String(status ?? '').trim();
  if (!value) return 'Unknown';
  if (value.toLowerCase() === 'waiting') return 'Processing';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

/** Badge for workflow statuses (processing / complete / error / archived). */
export function StatusBadge({ status, label, size = 'md', className, ...props }) {
  const tone = statusTone(status);
  return (
    <Badge tone={tone} dot pulse={tone === 'warning'} size={size} className={className} {...props}>
      {label ?? statusLabel(status)}
    </Badge>
  );
}
